import { ExplorerItem } from '@/types/types';
import { API_UPLOADS_PATH } from "@/app/api/constants";
import { ExplorerItemType } from "@/enum/explorerItemType.ts";
import { EventEmitterForPlayerEvents, PlayerAdapter } from 'peaks.js';
import { AudioTrackAdapter } from "@/features/AudioWorkstation/adapters/AudioTrackAdapter.ts";
import { safeVolume } from "@/utils/utils.ts";

export class AudioAdapter implements PlayerAdapter {
  public mediaElement: HTMLAudioElement;
  public context: AudioContext;
  public explorerItem: ExplorerItem | null = null;
  public listeners: Map<number, AudioTrackAdapter> = new Map();
  private eventEmitter?: EventEmitterForPlayerEvents;
  private currentTime: number = 0;
  private isPlayingState: boolean = false;
  private isSeekingState: boolean = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(
    audio: HTMLAudioElement,
    context: AudioContext,
  ) {
    this.mediaElement = audio;
    this.context = context;

    // Set up event listeners for the audio element
    this.mediaElement.addEventListener('timeupdate', this.handleTimeUpdate);
    this.mediaElement.addEventListener('play', this.handlePlay);
    this.mediaElement.addEventListener('pause', this.handlePause);
    this.mediaElement.addEventListener('seeking', this.handleSeeking);
    this.mediaElement.addEventListener('seeked', this.handleSeeked);
    this.mediaElement.addEventListener('ended', this.handleEnded);
  }

  // Initialize peaks.js player adapter
  async init(eventEmitter: EventEmitterForPlayerEvents): Promise<void> {
    this.eventEmitter = eventEmitter;

    /*if (this.audio.readyState >= 2) {
      this.eventEmitter.emit('player.canplay');
    } else {
      this.audio.addEventListener('canplay', () => {
        this.eventEmitter?.emit('player.canplay');
      }, { once: true });
    }*/
  }

  destroy() {
    /*console.log('AudioAdapter destroy')
    // this.audio.removeEventListener('timeupdate', this.handleTimeUpdate);
    this.audio.removeEventListener('play', this.handlePlay);
    this.audio.removeEventListener('pause', this.handlePause);
    this.audio.removeEventListener('seeking', this.handleSeeking);
    this.audio.removeEventListener('seeked', this.handleSeeked);
    this.audio.removeEventListener('ended', this.handleEnded);

    // Clean up all listeners
    this.listeners.clear();
    this.eventEmitter = undefined;*/
  }

  // Add a track adapter as a listener
  addListener(listener: AudioTrackAdapter) {
    this.listeners.set(listener.track.id, listener);
  }

  // Remove a track adapter listener
  removeListener(trackId: number) {
    this.listeners.delete(trackId);
  }

  handleLoadedMetadata(e: Event) {
    try {
      const target = e.target as HTMLAudioElement;
      target.play();
    } catch (error) {
      console.error('Error playing audio:', error);
    }
  }

  trackIsLoaded(explorerItem?: ExplorerItem | null): boolean {
    if ((this.mediaElement.readyState >= HTMLMediaElement.HAVE_METADATA && this.mediaElement.networkState === HTMLMediaElement.NETWORK_IDLE)
      && explorerItem && explorerItem.id === this.explorerItem?.id) {
      return true;
    }

    return false;
  }

  // Set the audio source and return a promise that resolves when the audio is loaded
  setSource(explorerItem: ExplorerItem): Promise<void> {
    if (explorerItem.type !== ExplorerItemType.TRACK) {
      return Promise.resolve();
    }

    // If we're already loading this exact item, return the existing promise
    if (this.loadingPromise && this.explorerItem?.id === explorerItem.id) {
      return this.loadingPromise;
    }

    this.explorerItem = explorerItem;
    this.mediaElement.src = `${API_UPLOADS_PATH}/${explorerItem.track.source}`;

    // Create a new loading promise
    this.loadingPromise = new Promise<void>((resolve, reject) => {
      const loadedMetaDataHandler = () => {
        this.mediaElement.removeEventListener('loadedmetadata', loadedMetaDataHandler);
        this.mediaElement.removeEventListener('error', errorHandler);
        resolve();
      };

      const errorHandler = (e: Event) => {
        this.mediaElement.removeEventListener('loadedmetadata', loadedMetaDataHandler);
        this.mediaElement.removeEventListener('error', errorHandler);
        reject(new Error(`Failed to load audio: ${(e as ErrorEvent).message}`));
      };

      // Listen for both loadedmetadata and error events
      // @note: the canplay event doesn't work on IOS?
      this.mediaElement.addEventListener('loadedmetadata', loadedMetaDataHandler, { once: true });
      this.mediaElement.addEventListener('error', errorHandler, { once: true });

      // If the audio is already in a state where it can be played, call the handler directly
      if (this.mediaElement.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        loadedMetaDataHandler();
      }
    });

    return this.loadingPromise;
  }

  // Play the main audio
  async play(explorerItem?: ExplorerItem | null): Promise<void> {
    // If a specific item is provided, set it as the source
    if (explorerItem && explorerItem.id !== this.explorerItem?.id) {
      await this.setSource(explorerItem);
    } else if (this.explorerItem && !this.trackIsLoaded(this.explorerItem)) {
      // If we have an explorer item set but it's not loaded, ensure it loads
      await this.setSource(this.explorerItem);
    }

    // Try to play - this will return a promise that resolves when playback has successfully started
    try {
      await this.mediaElement.play();
    } catch (error) {
      console.error('Error playing audio:', error);
      throw error;
    }
  }

  // Pause the main audio
  pause() {
    if (!this.isPlayingState) return;
    this.mediaElement.pause();
  }

  stop() {
    if (!this.isPlayingState) return;

    this.mediaElement.pause();
    this.mediaElement.currentTime = 0;
  }

  // Seek to a specific time
  seek(time: number) {
    this.mediaElement.currentTime = time;
    this.currentTime = time;

    return this;
  }

  // Get the current playback time
  getCurrentTime(): number {
    return this.mediaElement.currentTime;
  }

  setCurrentTime(time: number): this {
    this.mediaElement.currentTime = time;

    return this;
  }

  getTimeLeft(): number {
    return this.mediaElement.duration - this.mediaElement.currentTime;
  }

  fastForward(amount = 10): void {
    this.setCurrentTime(this.getCurrentTime() + amount);
  }

  fastRewind(amount = 10): void {
    this.setCurrentTime(this.getCurrentTime() - amount);
  }

  // Check if audio is currently playing
  isPlaying(): boolean {
    return this.isPlayingState;
  }

  // Check if audio is currently seeking
  isSeeking(): boolean {
    return this.isSeekingState;
  }

  // Get the duration of the audio
  getDuration(): number {
    return this.mediaElement.duration || 0;
  }

  getVolume() {
    return this.mediaElement.volume || 0;
  }

  setVolume(value: number) {
    this.mediaElement.volume = safeVolume(value);

    return this;
  }

  isMuted() {
    return this.mediaElement.muted;
  }

  setMuted(muted: boolean) {
    this.mediaElement.muted = muted;

    return this;
  }

  getExplorerItem(): ExplorerItem | null {
    return this.explorerItem;
  }

  getAudioContext(): AudioContext {
    return this.context;
  }

  // Handle play event
  private handlePlay = () => {
    this.isPlayingState = true;
    if (this.eventEmitter) {
      this.eventEmitter.emit('player.playing', this.currentTime);
    }

    this.listeners.forEach((listener) => listener.play())
  }

  // Handle pause event
  private handlePause = () => {
    this.isPlayingState = false;

    if (this.eventEmitter) {
      this.eventEmitter.emit('player.pause', this.currentTime);
    }

    // Pause all track adapters
    this.listeners.forEach((listener) => listener.pause());
  }

  // Handle seeking state
  private handleSeeking = () => {
    this.isSeekingState = true;
    this.listeners.forEach((listener) => listener.updatePlaybackTime())
  }

  // Handle seeked event
  private handleSeeked = () => {
    this.isSeekingState = false;
    this.currentTime = this.mediaElement.currentTime;

    if (this.eventEmitter) {
      this.eventEmitter.emit('player.seeked', this.currentTime);
      this.eventEmitter.emit('player.timeupdate', this.currentTime);
      this.listeners.forEach((listener) => listener.updateStartTime())
    }
  }

  // Handle ended event
  private handleEnded = () => {
    if (this.eventEmitter) {
      this.eventEmitter.emit('player.ended');
    }
  }

  private handleTimeUpdate = () => {
    this.currentTime = this.mediaElement.currentTime;

    if (this.eventEmitter && !this.isSeekingState) {
      // @note: this syncs the Peaks.js playhead with the audio current time.
      this.eventEmitter.emit('player.timeupdate', this.currentTime);
      this.listeners.forEach((listener) => listener.updatePlaybackTime())
    }
  }
}
